/**
 * Message-row preview windowing + match highlighting.
 *
 * A raw payload is far wider than the row, so a naive `slice(0, 60)` hides the
 * very match the user searched for and the row reads as a false positive. The
 * window is therefore centred on the first hit.
 */

export interface PreviewSegment {
  text: string;
  hit: boolean;
}

/** Characters of context kept before the match inside the window. Deliberately
 *  short: the row clips with an ellipsis at whatever width the pane happens to
 *  be, so a generous lead pushes the match itself out of sight. */
const LEAD = 8;

interface Range {
  start: number;
  end: number;
}

function indexOfAll(haystack: string, needle: string): Range[] {
  const out: Range[] = [];
  if (!needle) return out;
  let from = 0;
  while (from <= haystack.length - needle.length) {
    const at = haystack.indexOf(needle, from);
    if (at === -1) break;
    out.push({ start: at, end: at + needle.length });
    from = at + needle.length;
  }
  return out;
}

/** Whitespace-stripped copy plus a map from stripped index → original index,
 *  so a match found in the compact form can be highlighted in place. */
function stripWhitespace(s: string): { text: string; map: number[] } {
  let text = '';
  const map: number[] = [];
  for (let i = 0; i < s.length; i++) {
    const c = s[i]!;
    if (/\s/.test(c)) continue;
    text += c;
    map.push(i);
  }
  return { text, map };
}

function findRanges(haystackLower: string, terms: readonly string[]): Range[] {
  const ranges: Range[] = [];
  let stripped: { text: string; map: number[] } | null = null;

  for (const term of terms) {
    const needle = term.toLowerCase();
    if (!needle.trim()) continue;

    const direct = indexOfAll(haystackLower, needle);
    if (direct.length > 0) {
      ranges.push(...direct);
      continue;
    }
    // Mirrors matchesText: a fragment copied from the pretty-printed pane
    // carries whitespace the compact wire payload doesn't have.
    if (!/\s/.test(needle)) continue;
    const compactNeedle = needle.replace(/\s+/g, '');
    if (!compactNeedle) continue;
    stripped ??= stripWhitespace(haystackLower);
    for (const r of indexOfAll(stripped.text, compactNeedle)) {
      const start = stripped.map[r.start];
      const end = stripped.map[r.end - 1];
      if (start === undefined || end === undefined) continue;
      ranges.push({ start, end: end + 1 });
    }
  }

  if (ranges.length < 2) return ranges;
  ranges.sort((a, b) => a.start - b.start);
  const merged: Range[] = [ranges[0]!];
  for (const r of ranges.slice(1)) {
    const last = merged[merged.length - 1]!;
    if (r.start <= last.end) last.end = Math.max(last.end, r.end);
    else merged.push(r);
  }
  return merged;
}

/**
 * Build the row preview as highlight segments. When `terms` produce no hit the
 * behaviour is the original head-of-string truncation.
 */
export function buildPreview(
  raw: string,
  terms: readonly string[] = [],
  width = 60,
): PreviewSegment[] {
  const lower = raw.toLowerCase();
  const ranges = findRanges(lower, terms);

  const first = ranges[0];
  let start = 0;
  if (first && first.start > LEAD) {
    start = Math.min(first.start - LEAD, Math.max(0, raw.length - width));
  }
  const end = Math.min(raw.length, start + width);

  const segments: PreviewSegment[] = [];
  const push = (text: string, hit: boolean) => {
    if (!text) return;
    const last = segments[segments.length - 1];
    if (last && last.hit === hit) last.text += text;
    else segments.push({ text, hit });
  };

  if (start > 0) push('…', false);

  let cursor = start;
  for (const r of ranges) {
    if (r.end <= start) continue;
    if (r.start >= end) break;
    const hitStart = Math.max(r.start, start);
    const hitEnd = Math.min(r.end, end);
    push(raw.slice(cursor, hitStart), false);
    push(raw.slice(hitStart, hitEnd), true);
    cursor = hitEnd;
  }
  push(raw.slice(cursor, end), false);

  if (end < raw.length) push('…', false);

  return segments;
}
